import { useEffect, useState } from 'react';
import { Button, Popconfirm, message } from 'antd';
import { getTelegramMe, unlinkTelegram, type TelegramMeResponse } from './api';
import { TelegramNotifySettings } from './TelegramNotifySettings';
import { SettingRow, StatusChip } from '../profile/ProfileSection.tsx';

/**
 * Раздел «Telegram» в профиле: связан ли аккаунт и личные уведомления.
 * Связывание идёт только кнопкой из чата или из личной переписки с ботом —
 * данные аккаунта должен подписать сам Telegram, иначе связке нельзя
 * доверять (docs/modules/telegram-bot.md §5.1).
 */
export function TelegramProfileCard(): JSX.Element | null {
  const [state, setState] = useState<TelegramMeResponse | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getTelegramMe()
      .then(setState)
      .catch(() => setState(null));
  }, []);

  if (!state) return null;

  const handleUnlink = async () => {
    setBusy(true);
    try {
      await unlinkTelegram();
      setState({ ...state, linked: false, username: null, firstName: null, lastName: null, linkedAt: null });
      void message.success('Аккаунт Telegram отвязан');
    } catch (err) {
      void message.error(err instanceof Error ? err.message : 'Не удалось отвязать аккаунт');
    } finally {
      setBusy(false);
    }
  };

  const name = [state.firstName, state.lastName].filter(Boolean).join(' ');

  if (!state.linked) {
    return (
      <SettingRow
        testId="profile-telegram-card"
        title="Аккаунт Telegram"
        hint={
          <>
            Пока аккаунт не связан, записи из рабочих чатов создаются от имени бота, а уведомления не
            приходят. Связать — кнопкой «Связать аккаунт» в закреплённом сообщении бота в рабочем чате
            {state.botUsername ? ' или в переписке с ботом' : ''}.
          </>
        }
        aside={
          <>
            <StatusChip on={false}>Не связан</StatusChip>
            {state.botUsername && (
              <Button
                type="primary"
                href={`https://t.me/${state.botUsername}?start=link`}
                target="_blank"
                rel="noreferrer"
              >
                Открыть бота
              </Button>
            )}
          </>
        }
      />
    );
  }

  return (
    <>
      <SettingRow
        testId="profile-telegram-card"
        title="Аккаунт Telegram"
        hint={
          <>
            {name || 'Без имени'}
            {state.username ? ` · @${state.username}` : ''}
            {state.linkedAt ? ` · связан ${new Date(state.linkedAt).toLocaleDateString('ru-RU')}` : ''}.
            В записях из рабочих чатов автором указаны вы.
          </>
        }
        aside={
          <>
            <StatusChip on>Связан</StatusChip>
            <Popconfirm
              title="Отвязать Telegram?"
              description="Записи из чатов перестанут подписываться вашим именем, уведомления приходить не будут."
              okText="Отвязать"
              okButtonProps={{ danger: true }}
              cancelText="Отмена"
              onConfirm={handleUnlink}
            >
              <Button danger type="text" loading={busy}>
                Отвязать
              </Button>
            </Popconfirm>
          </>
        }
      />
      <TelegramNotifySettings botUsername={state.botUsername} />
    </>
  );
}
